import { create } from 'zustand';
import { BillingPayment } from '../types/payment';
import { GatewayStatsSummary, getAllPayments } from '../services';

export interface GatewayStatsDetailed {
    [gateway: string]: {
        count: number;
        total: number;
        percentage: number;
    };
}

export interface ServiceStats {
    [serviceName: string]: {
        count: number;
        percentage: number;
        color: string;
    };
}

interface PaymentsState {
    payments: BillingPayment[];
    gatewayStats: GatewayStatsDetailed;
    serviceStats: ServiceStats;
    gateways: string[];
    isLoading: boolean;
    error: string | null;
    fetchPayments: () => Promise<void>;
}

const serviceColors: Record<string, string> = {
    "kanema": "#FF0000",
    "venda": "#F9B959",
    "nkhani": "#F28D15",
    "katswiri": "#808191",
    "mingoli": "#999999"
};

export const usePaymentsStore = create<PaymentsState>((set, get) => ({
    payments: [],
    gatewayStats: {},
    serviceStats: {},
    gateways: [],
    isLoading: false,
    error: null,

    fetchPayments: async () => {
        set({ isLoading: true, error: null });
        try {
            const data = await getAllPayments();

            /** ---------------------
             * GATEWAY STATS
             * --------------------- */
            const gatewayStats = data.reduce((acc, p) => {
                if (p.payment_gateway === "PawaPay") {
                    if (p.mobile_operator === "TNM_MWI") {
                        acc.mpamba.count++;
                        acc.mpamba.total += p.amount;
                    } else if (p.mobile_operator === "AIRTEL_MWI") {
                        acc.airtel_money.count++;
                        acc.airtel_money.total += p.amount;
                    }
                } else if (p.payment_gateway === "PayPal") {
                    acc.paypal.count++;
                    acc.paypal.total += p.amount;
                } else if (p.payment_gateway === "card") {
                    acc.stripe.count++;
                    acc.stripe.total += p.amount;
                } else {
                    if (!acc.unknown) {
                        acc.unknown = { count: 0, total: 0, percentage: 0 };
                    }
                    acc.unknown.count++;
                    acc.unknown.total += p.amount;
                }
                return acc;
            }, {
                mpamba: { count: 0, total: 0, percentage: 0 },
                airtel_money: { count: 0, total: 0, percentage: 0 },
                paypal: { count: 0, total: 0, percentage: 0 },
                stripe: { count: 0, total: 0, percentage: 0 }
            } as GatewayStatsDetailed);

            const gatewayStatsValues = Object.values(gatewayStats) as {
                count: number;
                total: number;
                percentage: number;
            }[];
            const totalCountGateways = gatewayStatsValues.reduce((sum, g) => sum + g.count, 0);
            gatewayStatsValues.forEach((g) => {
                g.percentage = totalCountGateways > 0
                    ? parseFloat(((g.count / totalCountGateways) * 100).toFixed(2))
                    : 0;
            });

            /** ---------------------
             * SERVICE STATS
             * --------------------- */
            const serviceStats = data.reduce((acc, p) => {
                const rawService = p.service_name || "Unknown";
                const normalized = rawService.toLowerCase();

                if (!acc[normalized]) {
                    acc[normalized] = { count: 0, percentage: 0, color: serviceColors[normalized] || serviceColors["unknown"] };
                }
                acc[normalized].count++;
                return acc;
            }, {} as ServiceStats);

            const totalCountServices = data.length;
            Object.keys(serviceStats).forEach((service) => {
                serviceStats[service].percentage = totalCountServices > 0
                    ? parseFloat(((serviceStats[service].count / totalCountServices) * 100).toFixed(2))
                    : 0;
            });

            /** ---------------------
             * LIST OF GATEWAYS
             * --------------------- */
            const gateways = Array.from(
                new Set(data.map(p => p.payment_gateway || "Unknown"))
            );


            set({
                payments: data,
                gatewayStats,
                serviceStats,
                gateways,
                isLoading: false
            });

        } catch (err) {
            set({ error: 'Failed to fetch payments', isLoading: false });
        }
    }
}));

export function getGatewayStatsSummary(detailedStats: GatewayStatsDetailed): GatewayStatsSummary {
    return {
        mpamba: detailedStats['mpamba']?.percentage || 0,
        airtel_money: detailedStats['airtel_money']?.percentage || 0,
        paypal: detailedStats['paypal']?.percentage || 0,
        stripe: detailedStats['stripe']?.percentage || 0,
    };
}
